import { useMemo } from 'react';

import {
  faGrid2Plus,
  faSparkles,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleInfo, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, AlertDescription } from '@repo/videa-components';
import classNames from 'classnames';

import type { FmxSlot } from '@/@types/latestImagesV2';
import { FmxSortKey } from '@/modules/annotator/atoms';

interface FmxSortConfig {
  icon: IconProp;
  label: string;
  details: string | null;
}

interface Props {
  fmxIndexes?: FmxSlot[];
  fmxSortDescription?: string;
  fmxSortKey: string;
  isSelected: boolean;
  onClick: () => void;
}

export const FmxSortOption = ({
  fmxIndexes,
  fmxSortDescription,
  fmxSortKey,
  isSelected,
  onClick,
}: Props) => {
  const { t } = useLingui();

  const hasInvalidMetadata = !!fmxIndexes?.some((slot) =>
    slot.imageData?.some((image) => image.invalidMetadataReason),
  );

  const fmxSortConfigMap: Record<FmxSortKey, FmxSortConfig> = useMemo(
    () => ({
      [FmxSortKey.IMAGING_SOFTWARE_ORDER]: {
        icon: faGrid2Plus,
        label: t`Derived from capture software`,
        details: t`This will use image data, where possible, to show images the same way they were captured in the imaging platform. If they were initially taken correctly, this setting will match the imaging platform order.`,
      },
      [FmxSortKey.VIDEA_TEMPLATE_ORDER]: {
        icon: faSparkles,
        label: t`VideaAI smart sorting`,
        details: t`This automatically places images in the expected clinical order based on AI. This setting will perform better if images were not taken exactly according to the capture template originally.`,
      },
      [FmxSortKey.CURRENT_ORDER]: {
        icon: faCopy,
        label: t`Keep current sorting`,
        details: t`This preserves any existing adjustments you've made to this visit's image layout.`,
      },
    }),
    [t],
  );

  const currentFmxSortConfig = useMemo(() => {
    const config = fmxSortConfigMap[fmxSortKey as FmxSortKey];
    if (config) return config;

    // Fallback for unknown keys
    return {
      icon: faCopy,
      label: fmxSortDescription ?? fmxSortKey,
      details: null,
    };
  }, [fmxSortConfigMap, fmxSortDescription, fmxSortKey]);

  return (
    <button
      onClick={onClick}
      className={classNames('vai-text-left vai-p-3 vai-rounded-md vai-border', {
        'vai-border-zinc-800 hover:vai-border-zinc-700': !isSelected,
        'vai-border-sapphire-600': isSelected,
      })}
    >
      <FontAwesomeIcon
        size="xl"
        className={classNames({
          'vai-text-zinc-400': !isSelected,
          'vai-text-sapphire-600': isSelected,
        })}
        icon={currentFmxSortConfig.icon}
      />
      <p
        className={classNames('vai-mt-4 vai-font-inter vai-font-semibold', {
          'vai-text-zinc-400': !isSelected,
          'vai-text-zinc-200': isSelected,
        })}
      >
        {currentFmxSortConfig.label}
      </p>
      {currentFmxSortConfig.details && (
        <p
          className={classNames('vai-mt-2 vai-font-inter vai-text-sm', {
            'vai-text-zinc-400': !isSelected,
            'vai-text-zinc-200': isSelected,
          })}
        >
          {currentFmxSortConfig.details}
        </p>
      )}
      {hasInvalidMetadata && (
        <Alert className="vai-mt-4" variant="warning">
          <FontAwesomeIcon icon={faCircleInfo} />
          <AlertDescription>
            <Trans>
              We encountered an issue with some image data. The image layout may
              not appear as expected.
            </Trans>
          </AlertDescription>
        </Alert>
      )}
    </button>
  );
};
